import { Fragment, useState, useEffect, useRef } from "react"

// ** Third Party Components
import axios from "axios"
import { format } from "date-fns"
import { he } from "date-fns/locale"
import { parseCookies } from "nookies"
import { useQuery } from "@tanstack/react-query"
import { CloudSun, Plus, Trash2 } from "lucide-react"
import { useFieldArray, useFormContext } from "react-hook-form"

// ** Custom Components
import { Button } from "../ui/button"
import { Calendar } from "../../calendar"
import { SelectInput } from "../ui/SelectInput"
import { Popover, PopoverContent, PopoverTrigger } from "../../popover"
import { FormItem, FormLabel, FormControl, FormMessage, FormTextareaControl } from "../ui/form"

// ** Types
import { DictType } from "@pma/types"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Utils
import { WorkLogFormData } from "@pma/utils/work-diaries-schema"

// ** Stores
import { useAuth } from "@pma/stores/auth-store"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Routes
import { FINANCIAL_SERVER_URL, GET_CONTRACTOR_COMPANY_LIST } from "@pma/utils/api-routes"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Required Field Label
import { RequiredFieldLabel } from "../ui/required-field-label"

// ** Custom Components
import GeoCoder from "@pma/components/projects/geo-coder"
import InsertUpdateContractorCompanyDialog from "@pma/components/Contractor-Company/InsertUpdateContractorCompanyDialog"

interface GeneralFormProps {
  lstStatus: any[]
  lstWeather: any[]
  lstContractor: any[]
  workDiaries: DictType | undefined
  project?: DictType
}

export function GeneralForm({
  workDiaries,
  lstStatus,
  lstWeather,
  lstContractor,
  project,
}: GeneralFormProps) {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const orgId = authConfig?.last_organization_id

  const {
    setValue,
    getValues,
    register,
    formState: { errors },
    control,
    watch,
  } = useFormContext<WorkLogFormData>()

  const { fields, append, remove } = useFieldArray({ control, name: "workDetails" })

  const firstTime = useRef<boolean>(true)
  const [contractorCompanyDialog, setContractorCompanyDialog] = useState(false)
  const [newContractorCompanyDetails, setNewContractorCompanyDetails] = useState<any>(null)

  const getContractorCompanyListApi = async () => {
    const params: any = { organization_id: orgId }

    const response = await axios.get(FINANCIAL_SERVER_URL + GET_CONTRACTOR_COMPANY_LIST, {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return response.data
  }

  const { data: companies = [], refetch: refetchCompanies } = useQuery({
    queryKey: ["contractor-companies", orgId],
    queryFn: getContractorCompanyListApi,
  })

  // Calculate default contractor value
  const getDefaultContractorValue = () => {
    if (workDiaries?.contractor_company_id) return workDiaries.contractor_company_id

    // If creating new diary, check if project has default contractor
    if (project?._contractor_company?.id && companies?.length > 0) {
      const contractorExists = companies?.find(
        (contractor: any) => contractor.id === project?._contractor_company?.id,
      )
      if (contractorExists) return project._contractor_company.id
    }

    if (companies?.length > 0) return companies[0].id

    return undefined
  }

  // Set default contractor value when component mounts or data changes
  useEffect(() => {
    const currentValue = watch("company_id")

    if (currentValue && companies?.length > 0) {
      // Re-apply existing value after companies load to sync select display
      const exists = companies.find((c: any) => c.id === currentValue)
      if (exists) {
        setValue("company_id", currentValue, { shouldValidate: false })
      }
    } else if (firstTime.current && !currentValue) {
      const defaultValue = getDefaultContractorValue()
      if (defaultValue) {
        firstTime.current = false
        setValue("company_id", defaultValue, { shouldValidate: false })
      }
    }

    if (newContractorCompanyDetails) {
      setNewContractorCompanyDetails(null)
      setValue("company_id", newContractorCompanyDetails.id, { shouldValidate: false })
    }
  }, [project, workDiaries, companies, authConfig, setValue, watch, newContractorCompanyDetails])

  return (
    <Fragment>
      <div className='space-y-6'>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <FormItem error={errors.projectName}>
            <FormLabel>
              {translate("Project Name")}
              <RequiredFieldLabel tooltipText={translate("Project name is required for the work log")} />
            </FormLabel>
            <FormControl
              {...register("projectName")}
              disabled
              placeholder={translate("Project name")}
            />
            <FormMessage />
          </FormItem>

          <FormItem>
            <FormLabel>{translate("Log Number")}</FormLabel>
            <FormControl
              {...register("number")}
              disabled
              placeholder={translate("Log number is generated automatically based on project")}
            />
          </FormItem>
          <div className='flex items-end gap-2'>
            <SelectInput
              name='company_id'
              includeNoOption={companies?.length === 0 ? true : false}
              label={translate("Contractor Company")}
              options={companies?.map((obj: any) => ({
                value: obj.id,
                label: obj.name,
              }))}
              placeholder={translate("Select a Contractor")}
              defaultValue={getDefaultContractorValue()}
            />
            <button
              className='rounded-lg bg-gray-200 p-2'
              type='button'
              onClick={() => setContractorCompanyDialog(true)}
            >
              <Plus />
            </button>
          </div>
          <FormItem error={errors.date}>
            <FormLabel>
              {translate("Date")} <RequiredFieldLabel tooltipText={translate("Date is required for the work log")} />
            </FormLabel>
            <Popover>
              <div className='relative w-full'>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-600 hover:border-blue-500 focus:outline-none",
                      errors?.date && "border-red-500 focus-visible:ring-red-500",
                    )}
                  >
                    {watch("date")
                      ? format(watch("date"), "LLL dd, y", {
                        locale: isHebrew ? he : undefined,
                      })
                      : format(new Date(), "LLL dd, y", {
                        locale: isHebrew ? he : undefined,
                      })}
                  </button>
                </PopoverTrigger>
                {watch("date") && (
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation() // prevent Popover from opening
                      setValue("date", "", { shouldValidate: true })
                    }}
                    className={cn(
                      "absolute top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600",
                      isHebrew ? "left-2" : "right-2",
                    )}
                  >
                    &times;
                  </button>
                )}
              </div>

              <PopoverContent className='w-auto p-0' align='start'>
                <Calendar
                  initialFocus
                  mode='single'
                  defaultMonth={new Date()}
                  locale={isHebrew ? he : undefined}
                  selected={watch("date") ? new Date(watch("date")) : new Date()}
                  today={new Date()}
                  onSelect={(date: Date | undefined) => {
                    if (date) {
                      setValue("date", format(date, "yyyy-MM-dd"), { shouldValidate: true })
                    }
                  }}
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>

          <FormItem error={errors.siteAddress} className='mt-0'>
            <FormLabel>{translate("Site Address")}</FormLabel>
            <GeoCoder
              location={watch("siteAddress") || ""}
              setGeoLocation={(e) => setValue("siteAddress", e?.name || "")}
            />
            <FormMessage />
          </FormItem>

          <FormItem>
            <FormLabel>{translate("Status")}</FormLabel>
            <div className='flex h-10 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5'>
              <span className='text-sm text-gray-600'>
                {(() => {
                  // First check if there's an existing status value
                  const currentStatusId = getValues("status")
                  if (currentStatusId) {
                    const currentStatus = lstStatus.find((status) => status.id === currentStatusId)
                    if (currentStatus) {
                      return isHebrew
                        ? currentStatus.he && !["", null, undefined].includes(currentStatus.he)
                          ? currentStatus.he
                          : currentStatus.en
                        : currentStatus.en
                    }
                  }

                  // Fallback to draft status if no existing status
                  const draftStatus = lstStatus.find(
                    (status) =>
                      (status.en && status.en.toLowerCase() === "draft") ||
                      (status.he && status.he.toLowerCase() === "טיוטה"),
                  )
                  return isHebrew
                    ? draftStatus?.he && !["", null, undefined].includes(draftStatus.he)
                      ? draftStatus.he
                      : draftStatus?.en
                    : draftStatus?.en
                })()}
              </span>
            </div>
          </FormItem>
        </div>

        {/* Description */}
        <FormItem error={errors.description}>
          <FormLabel>{translate("Description and Content")}</FormLabel>
          <FormTextareaControl
            {...register("description")}
            placeholder={translate("Description of work performed")}
            className='min-h-[100px]'
          />
          <FormMessage />
        </FormItem>

        {/* Weather */}
        <div className='space-y-4 rounded-md border border-gray-200 p-3'>
          <div className='flex items-center gap-2'>
            <CloudSun className='h-5 w-5 text-gray-500' />
            <h3 className='font-medium'>{translate("Weather Conditions")}</h3>
          </div>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
            {/* Weather fields */}
            <SelectInput
              name='weather.description'
              label={translate("Description")}
              placeholder={translate("Select a description")}
              options={lstWeather.map((obj: any) => ({
                value: obj.id,
                label: isHebrew
                  ? ["", null, undefined].includes(obj.he)
                    ? obj.en
                    : obj.he
                  : obj.en,
              }))}
            />

            <FormItem error={errors.weather?.temperature}>
              <FormLabel>{translate("Temperature")}</FormLabel>
              <FormControl
                type='number'
                {...register("weather.temperature")}
                placeholder={`${translate("Example")}: 25°C`}
              />
              <FormMessage />
            </FormItem>

            <SelectInput
              name='weather.rating'
              label={`${translate("Rating")} (1–5)`}
              placeholder={translate("Select rating")}
              options={[
                { value: 1, label: `1 - ${translate("Poor")}` },
                { value: 2, label: `2 - ${translate("Fair")}` },
                { value: 3, label: `3 - ${translate("Good")}` },
                { value: 4, label: `4 - ${translate("Very Good")}` },
                { value: 5, label: `5 - ${translate("Excellent")}` },
              ]}
            />
          </div>
        </div>

        {/* Work Details */}
        <div className='space-y-4'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <h3 className='font-medium'>{translate("Work Details")}</h3>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => append({ description: "", amount: 0, units: "", actionType: "add" })}
            >
              <Plus className='mr-2 h-4 w-4' />
              {translate("Add Item")}
            </Button>
          </div>

          <div className='space-y-3'>
            {fields.map((field, index) =>
              field.actionType === "delete" ? null : (
                <div
                  key={field.id}
                  className='grid grid-cols-1 items-start gap-3 rounded-md border border-gray-200 bg-white p-3 sm:grid-cols-[1fr_120px_120px_40px]'
                >
                  {/* Description */}
                  <FormItem error={errors.workDetails?.[index]?.description}>
                    <FormLabel>
                      {translate("Description")} <RequiredFieldLabel tooltipText={translate("Item description is required")} />
                    </FormLabel>
                    <FormControl
                      {...register(`workDetails.${index}.description`)}
                      placeholder={`${translate("Example")}: ${translate("Trench excavation")}`}
                    />
                    <FormMessage />
                  </FormItem>

                  {/* Quantity */}
                  <FormItem error={errors.workDetails?.[index]?.amount}>
                    <FormLabel>
                      {translate("Quantity")} <RequiredFieldLabel tooltipText={translate("Quantity is required")} />
                    </FormLabel>
                    <FormControl
                      type='number'
                      {...register(`workDetails.${index}.amount`, {
                        valueAsNumber: true,
                      })}
                      placeholder={translate("Quantity")}
                    />
                    <FormMessage />
                  </FormItem>

                  {/* Unit */}
                  <FormItem error={errors.workDetails?.[index]?.units}>
                    <FormLabel>
                      {translate("Unit")} <RequiredFieldLabel tooltipText={translate("Unit is required")} />
                    </FormLabel>
                    <FormControl
                      {...register(`workDetails.${index}.units`)}
                      placeholder={translate('m, pc, hr *')}
                    />
                    <FormMessage />
                  </FormItem>

                  {/* Remove Button */}
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='mt-1'
                    onClick={() => {
                      const currentDetails = getValues("workDetails")
                      const item = currentDetails && currentDetails[index]

                      if (item?.id && currentDetails) {
                        // Mark existing item for deletion
                        const updated = [...currentDetails]
                        updated[index] = { ...updated[index], actionType: "delete" }
                        setValue("workDetails", updated)
                      } else {
                        // Remove newly added (unsaved) item
                        remove(index)
                      }
                    }}
                  >
                    <Trash2 className='h-4 w-4 text-red-500' />
                  </Button>
                </div>
              ),
            )}
          </div>
        </div>
      </div>

      {contractorCompanyDialog && (
        <InsertUpdateContractorCompanyDialog
          refetchList={refetchCompanies}
          insertUpdateContractorCompanyDialog={contractorCompanyDialog}
          setNewContractorCompanyDetails={setNewContractorCompanyDetails}
          setInsertUpdateContractorCompanyDialog={setContractorCompanyDialog}
        />
      )}
    </Fragment>
  )
}
